import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api/client.js";
import type { SafeUser } from "../api/types.js";

export function SignUpPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // FIX-012: read directly from the signup response — never assumed,
  // never a hardcoded claim either way. `null` until the response arrives.
  const [emailDelivered, setEmailDelivered] = useState<boolean | null>(null);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post<{ user: SafeUser; emailDelivered: boolean }>("/auth/signup", null, {
        email,
        password,
        ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
      });
      setEmailDelivered(response.emailDelivered);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign up failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <h2>Account created</h2>
          {emailDelivered ? (
            <p className="muted">
              Your account (<strong>{email}</strong>) was created. Check your inbox for a verification email from VIDEO STUDIO and follow the link to
              verify your account, then come back and sign in.
            </p>
          ) : (
            <p className="muted">
              Your account (<strong>{email}</strong>) was created, but the verification email could not be delivered. Contact an operator to have your
              account verified, then come back and sign in.
            </p>
          )}
          <div className="auth-links">
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h2>Create your account</h2>
        <p className="muted">Sign up to get started with Emma OS GENERATE.</p>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className="field">
            <label htmlFor="displayName">Name (optional)</label>
            <input id="displayName" type="text" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" required autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input id="password" type="password" required minLength={12} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input id="confirmPassword" type="password" required minLength={12} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={submitting} style={{ width: "100%" }}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>
        <div className="auth-links">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
